import { X } from 'lucide-react';
import { type ReactElement, type ReactNode,useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/lib/cn';

interface ModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly description?: string | undefined;
  readonly children: ReactNode;
  readonly footer?: ReactNode | undefined;
  readonly closeOnBackdrop?: boolean | undefined;
  readonly maxWidthClass?: string | undefined;
}

/**
 * Minimal accessible modal: portal + backdrop + Escape close + focus return.
 * Built without Radix Dialog to avoid adding a runtime dependency for the
 * single confirmation surface we need right now.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  closeOnBackdrop = true,
  maxWidthClass = 'max-w-md',
}: ModalProps): ReactElement | null {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    window.setTimeout(() => {
      dialogRef.current?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = originalOverflow;
      previouslyFocusedRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="presentation"
    >
      <button
        type="button"
        aria-label="Cerrar diálogo"
        tabIndex={-1}
        onClick={() => {
          if (closeOnBackdrop) onClose();
        }}
        className="absolute inset-0 cursor-default bg-background/70 backdrop-blur-sm"
      />
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby={description !== undefined ? 'modal-description' : undefined}
        className={cn(
          'relative z-10 w-full overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-soft-3',
          'animate-fade-in',
          maxWidthClass,
        )}
      >
        <div className="flex items-start justify-between gap-4 p-6 pb-4">
          <div>
            <h2
              id="modal-title"
              className="font-display text-lg font-semibold tracking-tight"
            >
              {title}
            </h2>
            {description !== undefined ? (
              <p id="modal-description" className="mt-1 text-sm text-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 pb-4">{children}</div>
        {footer !== undefined ? (
          <div className="flex justify-end gap-2 border-t border-border bg-muted/30 px-6 py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
